import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import {
  FaBox,
  FaShoppingCart,
  FaDollarSign,
  FaExclamationTriangle,
  FaTags,
  FaLayerGroup,
  FaSync,
  FaPlus,
} from "react-icons/fa";
import {
  AreaChart,
  Area,
  Line,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "../Admin/dashboard.css";
import dashboardService from "../../services/dashboardService";
import { toast } from "react-toastify";
import { Col, Row, Button } from "react-bootstrap";

const VendorDashboard = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [period, setPeriod] = useState("30d");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // States for dashboard data
  const [stats, setStats] = useState(null);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [orderStatusData, setOrderStatusData] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [lowStockProducts, setLowStockProducts] = useState([]);

  // Color scheme for charts
  const CHART_COLORS = {
    revenue: "#10b981",
    orders: "#3b82f6",
    products: "#00bfa5",
    inventory: "#ef4444",
  };

  const getStatusColor = (status) => {
    const colors = {
      placed: "#6366f1",
      accepted: "#3b82f6",
      processing: "#8b5cf6",
      packed: "#ec4899",
      out_for_delivery: "#f59e0b",
      delivered: "#10b981",
      cancelled: "#ef4444",
      returned: "#64748b",
    };
    return colors[status] || "#64748b";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", { month: "short", day: "numeric" });
  };

  const toTitleCase = (str) => {
    if (!str) return "";
    return str
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat("en-IN").format(num || 0);
  };

  const formatStockWithUnit = (quantity, unit) => {
    const qty = Math.max(0, parseInt(quantity) || 0);
    const unitStr = unit ? ` ${unit}` : "";
    return `${qty}${unitStr}`;
  };

  const fetchDashboardData = useCallback(async (isInitialLoad = false) => {
    // Verify if user is authenticated
    const token = localStorage.getItem("authToken");
    if (!token || !user) {
      if (isInitialLoad) setLoading(false);
      return;
    }

    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }

      const useCustomDates = startDate || endDate;
      const dateParams = useCustomDates ? [startDate || null, endDate || null] : [];

      const [
        statsRes,
        revenueRes,
        orderStatusRes,
        productsRes,
        ordersRes,
        lowStockRes,
      ] = await Promise.all([
        dashboardService.getVendorDashboardStats(),
        dashboardService.getVendorRevenueTrend(period, ...dateParams),
        dashboardService.getVendorOrderStatusDistribution(period, ...dateParams),
        dashboardService.getVendorTopProducts(period, 5, ...dateParams),
        dashboardService.getVendorRecentOrders(5),
        dashboardService.getVendorLowStockProducts(5),
      ]);

      setStats(statsRes);

      const trendData = revenueRes?.trend || [];
      setRevenueTrend(
        trendData.map((item) => ({
          date: formatDate(item.date),
          revenue: parseFloat(item.revenue || 0),
          orders: parseInt(item.order_count || 0),
        }))
      );

      setOrderStatusData(orderStatusRes?.distribution || []);
      setTopProducts(productsRes?.products || []);
      setRecentOrders(ordersRes?.orders || []);
      setLowStockProducts(lowStockRes?.products || []);
    } catch (error) {
      console.error("Error fetching vendor dashboard data:", error);
      const isUnauthorized = error.response?.status === 401;
      const isOnLoginPage = window.location.pathname === "/login";

      if (token && !isUnauthorized && !isOnLoginPage) {
        toast.error("Failed to load dashboard data");
      }
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [period, startDate, endDate, user]);

  useEffect(() => {
    if (user) {
      fetchDashboardData(true);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      const timeoutId = setTimeout(() => {
        fetchDashboardData(false);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [startDate, endDate, period]);

  const periodButtons = [
    { label: "7 Days", value: "7d" },
    { label: "30 Days", value: "30d" },
    { label: "90 Days", value: "90d" },
    { label: "1 Year", value: "1y" },
  ];

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const kpiStats = [
    {
      title: "My Products",
      description: "Manage your entire inventory",
      value: formatNumber(stats?.totalProducts),
      icon: <FaBox />,
      color: CHART_COLORS.products,
      path: "/product",
    },
    {
      title: "Orders Scoped",
      description: "Orders containing my products",
      value: formatNumber(stats?.totalOrders),
      icon: <FaShoppingCart />,
      color: CHART_COLORS.orders,
      path: "/orders",
    },
    {
      title: "My Revenue",
      description: "Earnings from paid orders",
      value: `₹${formatNumber(stats?.totalRevenue)}`,
      icon: <FaDollarSign />,
      color: CHART_COLORS.revenue,
      path: "/product",
    },
    {
      title: "Low Stock Items",
      description: "Products requiring restocking",
      value: formatNumber(stats?.lowStockProducts),
      icon: <FaExclamationTriangle />,
      color: CHART_COLORS.inventory,
      path: "/product",
    },
  ];

  return (
    <div className="dashboard">
      {/* Header Section */}
      <div className="dashboard-header" style={{ background: "linear-gradient(135deg, #10b981 0%, #047857 100%)", border: "1px solid rgba(255, 255, 255, 0.05)" }}>
        <div>
          <h1 style={{ color: "#ffffff", marginBottom: "8px" }}>Welcome to Vendor Space, {user.first_name || 'Vendor'}!</h1>
          <p style={{ color: "rgba(255, 255, 255, 0.8)" }}>Review your catalog analytics, store sales performance, and tracking metrics</p>
        </div>
        <div className="header-actions">
          <div className="period-selector">
            {periodButtons.map((btn) => (
              <button
                key={btn.value}
                className={`period-btn ${period === btn.value ? "active" : ""}`}
                onClick={() => setPeriod(btn.value)}
              >
                {btn.label}
              </button>
            ))}
          </div>
          <div className="date-filters d-flex gap-2 align-items-center">
            <input
              type="date"
              className="form-control form-control-sm"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ maxWidth: "140px", background: "rgba(255,255,255,0.05)", color: "#fff", borderColor: "rgba(255,255,255,0.1)" }}
            />
            <span style={{ color: "rgba(255, 255, 255, 0.8)" }}>to</span>
            <input
              type="date"
              className="form-control form-control-sm"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ maxWidth: "140px", background: "rgba(255,255,255,0.05)", color: "#fff", borderColor: "rgba(255,255,255,0.1)" }}
            />
            {(startDate || endDate) && (
              <button
                className="btn btn-sm btn-outline-light"
                onClick={() => {
                  setStartDate("");
                  setEndDate("");
                }}
              >
                Clear
              </button>
            )}
          </div>
          <button
            className="refresh-btn"
            onClick={() => fetchDashboardData(false)}
            disabled={isRefreshing}
            style={{ backgroundColor: "#ffffff", color: "#10b981" }}
          >
            <FaSync className={isRefreshing ? "spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="stats-grid">
        {kpiStats.map((stat, index) => (
          <div
            key={index}
            className="stat-card"
            onClick={() => navigate(stat.path)}
            style={{ cursor: "pointer" }}
          >
            <div className="stat-header">
              <div className="stat-icon" style={{ backgroundColor: stat.color }}>
                {stat.icon}
              </div>
              <div className="stat-change text-start ps-3" style={{ flex: "2" }}>
                <span className="stat-title">{stat.title}</span>
                <h3 className="stat-value" style={{ color: stat.color }}>{stat.value}</h3>
              </div>
            </div>
            <div className="stat-description mt-2">
              <span>{stat.description}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="charts-row">
        {/* Sales Trend Chart */}
        <div className="chart-container">
          <div className="chart-header">
            <h3>My Sales Trend</h3>
            <p>Product sales revenue and order counts over time</p>
          </div>
          <div style={{ width: "100%", height: 300 }}>
            {revenueTrend.length > 0 ? (
              <ResponsiveContainer>
                <AreaChart data={revenueTrend} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorVendorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLORS.revenue} stopOpacity={0.8} />
                      <stop offset="95%" stopColor={CHART_COLORS.revenue} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={11} tickLine={false} />
                  <YAxis yAxisId="left" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis yAxisId="right" orientation="right" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} />
                  <Legend verticalAlign="top" height={36} />
                  <Area yAxisId="left" type="monotone" dataKey="revenue" name="Revenue (₹)" stroke={CHART_COLORS.revenue} fillOpacity={1} fill="url(#colorVendorRevenue)" />
                  <Line yAxisId="right" type="monotone" dataKey="orders" name="Orders Count" stroke={CHART_COLORS.orders} strokeWidth={2} activeDot={{ r: 8 }} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                No transaction trend recorded for the selected period.
              </div>
            )}
          </div>
        </div>

        {/* Order Status Distribution Chart */}
        <div className="chart-container">
          <div className="chart-header">
            <h3>Fulfillment Status</h3>
            <p>Breakdown of my product orders status</p>
          </div>
          <div style={{ width: "100%", height: 300 }} className="d-flex align-items-center justify-content-center">
            {orderStatusData.length > 0 ? (
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={orderStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={3}
                    dataKey="count"
                    nameKey="status"
                    label={({ name, percent }) => `${toTitleCase(name)} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {orderStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={getStatusColor(entry.status)} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value, name) => [value, toTitleCase(name)]} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <span className="text-muted">No orders containing your items in this period.</span>
            )}
          </div>
        </div>
      </div>

      {/* Grid for lists & tables */}
      <div className="dashboard-grid">
        {/* Recent orders table */}
        <div className="dashboard-section">
          <div className="section-header d-flex justify-content-between align-items-center">
            <div>
              <h2>Recent Orders</h2>
              <p>Transactions containing my catalog products</p>
            </div>
            <Link to="/orders" className="view-all-link">
              Go to Orders &rarr;
            </Link>
          </div>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>My Share</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.length > 0 ? (
                  recentOrders.map((order) => (
                    <tr key={order.id}>
                      <td>
                        <span className="fw-semibold">
                          #{order.id.substring(0, 8)}...
                        </span>
                      </td>
                      <td>{order.customer_name}</td>
                      <td>{formatDate(order.created_at)}</td>
                      <td>₹{formatNumber(order.amount)}</td>
                      <td>
                        <span className="status-badge" style={{ backgroundColor: getStatusColor(order.status) + "20", color: getStatusColor(order.status) }}>
                          {toTitleCase(order.status)}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="no-data">No product sales found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Low stock alerts list */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2>Low Stock Items</h2>
            <p>Products that require restocking</p>
          </div>
          <div className="alert-list">
            {lowStockProducts.length > 0 ? (
              lowStockProducts.map((prod) => (
                <div key={prod.id} className="alert-item" style={{ borderLeftColor: CHART_COLORS.inventory }}>
                  <div className="alert-icon" style={{ backgroundColor: CHART_COLORS.inventory }}>
                    <FaExclamationTriangle />
                  </div>
                  <div className="alert-content">
                    <p className="alert-product-name">{prod.name}</p>
                    <p className="alert-stock-info">
                      Current Stock: <strong style={{ color: CHART_COLORS.inventory }}>{formatStockWithUnit(prod.stock_quantity, prod.unit)}</strong> (Limit: {prod.low_stock_alert || 10})
                    </p>
                  </div>
                  <Link to={`/product/edit/${prod.id}`} className="alert-action">
                    Restock
                  </Link>
                </div>
              ))
            ) : (
              <div className="no-alerts">
                <p>All items in stock are at sufficient levels.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Products Visual */}
      <div className="dashboard-section mt-4">
        <div className="section-header">
          <h2>Top Selling Products</h2>
          <p>My best performing listings by quantity sold</p>
        </div>
        <Row className="align-items-center">
          <Col md={7}>
            <div style={{ width: "100%", height: 260 }}>
              {topProducts.length > 0 ? (
                <ResponsiveContainer>
                  <BarChart data={topProducts} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                    <XAxis type="number" stroke="#94a3b8" fontSize={11} tickLine={false} />
                    <YAxis dataKey="name" type="category" stroke="#94a3b8" fontSize={10} width={90} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }} />
                    <Bar dataKey="total_quantity" name="Quantity Sold" fill={CHART_COLORS.products} radius={[0, 4, 4, 0]} barSize={12} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="d-flex align-items-center justify-content-center h-100 text-muted">
                  No sales recorded.
                </div>
              )}
            </div>
          </Col>
          <Col md={5}>
            <div className="pe-3">
              <h4 style={{ fontSize: "1.1rem", fontWeight: "700", marginBottom: "15px" }}>Popular Items Overview</h4>
              {topProducts.length > 0 ? (
                topProducts.map((prod, index) => (
                  <div key={prod.id} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                    <div className="d-flex align-items-center gap-2">
                      <span className="badge rounded-circle bg-light text-dark px-2">{index + 1}</span>
                      <span style={{ fontSize: "0.9rem", fontWeight: "500", color: "#334155", maxWidth: "180px" }} className="text-truncate">{prod.name}</span>
                    </div>
                    <div className="text-end">
                      <span style={{ fontSize: "0.85rem", fontWeight: "700", color: "#1e293b", display: "block" }}>{prod.total_quantity} sold</span>
                      <span style={{ fontSize: "0.75rem", color: "#64748b" }}>₹{formatNumber(prod.total_revenue)} revenue</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-muted" style={{ fontSize: "0.9rem" }}>Sell items to see performance analytics.</p>
              )}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default VendorDashboard;
